import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingContainer } from '@/components/ui';
import { useIndicadoresByEmpresa } from '@/hooks/useIndicadores';
import { ordenarPorPrioridad, IndicadorEmpresa, TipoResultadoIndicador } from '@/services/indicadores.service';

interface IndicadoresGridProps {
  nit: string;
}

function formatValor(v: number, tipo: TipoResultadoIndicador): string {
  if (tipo === 'MONEDA') {
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(v);
  }
  const formattedNum = Number.isFinite(v) ? parseFloat(v.toFixed(2)).toString() : '—';
  if (tipo === 'NUMERO') {
    return formattedNum;
  }
  return `${formattedNum}%`;
}

function formatFrecuencia(frec: string): string {
  if (!frec) return '';
  const lower = frec.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function renderUltimaRevision(ind: IndicadorEmpresa) {
  const { ultimaEvaluacion, frecuencia } = ind;
  if (!ultimaEvaluacion) {
    return <span className="text-gray-400 font-medium text-[11px]">Sin evaluar</span>;
  }

  const date = new Date(ultimaEvaluacion);
  const diffTime = Date.now() - date.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  const limitDaysMap: Record<string, number> = {
    MENSUAL: 30,
    TRIMESTRAL: 90,
    SEMESTRAL: 180,
    ANUAL: 365,
  };
  const limit = limitDaysMap[frecuencia] || 30;
  const remaining = limit - diffDays;

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[12px] text-gray-700">
        {date.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' })}
      </span>
      <span className="text-[10px] text-gray-400">{diffDays === 0 ? 'Hoy' : `Hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`}</span>
      {remaining <= 0 ? (
        <span className="text-red-500 font-semibold text-[10px] uppercase">
          Vencido hace {Math.abs(remaining)} día{Math.abs(remaining) !== 1 ? 's' : ''}
        </span>
      ) : (
        <span className="text-green-600 text-[10px]">
          Vence en {remaining} día{remaining !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
}

export function IndicadoresGrid({ nit }: { nit: string }) {
  const { data: indicadores, isLoading, error } = useIndicadoresByEmpresa(nit);
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [hoveredRect, setHoveredRect] = useState<DOMRect | null>(null);

  // Filters state
  const [filterEstado, setFilterEstado] = useState<string>('TODOS');
  const [filterFrecuencia, setFilterFrecuencia] = useState<string>('TODAS');

  const filteredIndicadores = useMemo(() => {
    if (!indicadores) return [];
    let result = [...indicadores];

    // Filter by compliance state
    if (filterEstado !== 'TODOS') {
      if (filterEstado === 'SIN_EVALUAR') {
        result = result.filter((ind) => !ind.ultimaEvaluacion);
      } else if (filterEstado === 'EVALUADO') {
        result = result.filter((ind) => !!ind.ultimaEvaluacion);
      } else if (filterEstado === 'CUMPLE') {
        result = result.filter((ind) => !!ind.ultimaEvaluacion && ind.estado === 'CUMPLE');
      } else if (filterEstado === 'NO_CUMPLE') {
        result = result.filter((ind) => !!ind.ultimaEvaluacion && ind.estado === 'NO_CUMPLE');
      }
    }

    // Filter by frequency
    if (filterFrecuencia !== 'TODAS') {
      result = result.filter((ind) => ind.frecuencia === filterFrecuencia);
    }

    return ordenarPorPrioridad(result);
  }, [indicadores, filterEstado, filterFrecuencia]);

  const activeHoveredInd = useMemo(() => {
    if (hoveredId === null || !indicadores) return null;
    return indicadores.find((ind) => ind.id === hoveredId) || null;
  }, [hoveredId, indicadores]);

  const getTooltipStyle = (rect: DOMRect) => {
    const tooltipWidth = 320;
    const tooltipHeight = 240;
    let left = rect.left;
    let top = rect.bottom + 6;

    // Check right screen boundary
    if (left + tooltipWidth > window.innerWidth - 16) {
      left = window.innerWidth - tooltipWidth - 16;
    }
    // Check left screen boundary
    if (left < 16) {
      left = 16;
    }

    // Check bottom screen boundary
    if (top + tooltipHeight > window.innerHeight - 16) {
      // Place it above the element
      top = rect.top - tooltipHeight - 6;
      // If it goes above the top boundary of screen, fallback to bottom placement
      if (top < 16) {
        top = rect.bottom + 6;
      }
    }

    return {
      position: 'fixed' as const,
      top: `${top}px`,
      left: `${left}px`,
      width: `${tooltipWidth}px`,
      zIndex: 9999,
      pointerEvents: 'none' as const,
      backdropFilter: 'blur(8px)',
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    };
  };

  if (isLoading) {
    return <LoadingContainer text="Cargando indicadores..." />;
  }

  if (error) {
    return (
      <div className="admin-card p-8 text-center">
        <p className="text-[13px] font-medium" style={{ color: 'var(--admin-red)' }}>
          Error al cargar los indicadores
        </p>
      </div>
    );
  }

  if (!indicadores || indicadores.length === 0) {
    return (
      <div className="admin-card p-8 text-center">
        <p className="text-[13px]" style={{ color: 'var(--admin-text3)' }}>
          No hay indicadores registrados
        </p>
      </div>
    );
  }

  return (
    <div className="admin-card">
      <div className="admin-card-header flex items-center justify-between">
        <span className="text-[14px] font-semibold text-gray-800">Panel de Indicadores a Evaluar</span>
        <span className="text-[11px] text-gray-400 italic">Pasa el cursor sobre el nombre para ver detalles</span>
      </div>

      {/* Filter Bar */}
      <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-6 bg-[#fcfbf9]">
        <div className="flex items-center gap-2">
          <label htmlFor="filter-estado" className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Estado:
          </label>
          <select
            id="filter-estado"
            value={filterEstado}
            onChange={(e) => setFilterEstado(e.target.value)}
            className="admin-search-input py-1 px-3 text-xs w-40 rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
            style={{ minHeight: '32px' }}
          >
            <option value="TODOS">Todos</option>
            <option value="SIN_EVALUAR">Sin evaluar</option>
            <option value="EVALUADO">Evaluados</option>
            <option value="CUMPLE">Cumple</option>
            <option value="NO_CUMPLE">No cumple</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="filter-frecuencia" className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Frecuencia:
          </label>
          <select
            id="filter-frecuencia"
            value={filterFrecuencia}
            onChange={(e) => setFilterFrecuencia(e.target.value)}
            className="admin-search-input py-1 px-3 text-xs w-40 rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
            style={{ minHeight: '32px' }}
          >
            <option value="TODAS">Todas</option>
            <option value="MENSUAL">Mensual</option>
            <option value="TRIMESTRAL">Trimestral</option>
            <option value="SEMESTRAL">Semestral</option>
            <option value="ANUAL">Anual</option>
          </select>
        </div>

        <div className="text-[11px] text-gray-400 sm:ml-auto font-medium">
          Mostrando {filteredIndicadores.length} de {indicadores.length} indicadores
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="admin-table w-full">
          <thead>
            <tr>
              <th className="text-left py-3 px-4">Nombre</th>
              <th className="text-left py-3 px-4">Frecuencia</th>
              <th className="text-left py-3 px-4">Estado</th>
              <th className="text-left py-3 px-4">Resultado</th>
              <th className="text-left py-3 px-4">Meta</th>
              <th className="text-left py-3 px-4">Última Revisión</th>
              <th className="text-right py-3 px-4">Acción</th>
            </tr>
          </thead>
          <tbody>
            {filteredIndicadores.map((ind) => (
              <tr key={ind.id} className="hover:bg-gray-50/60 transition-colors border-b border-gray-100 last:border-0">
                <td
                  className="py-3 px-4 font-medium text-gray-800 relative align-middle max-w-xs cursor-help"
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoveredRect(rect);
                    setHoveredId(ind.id);
                  }}
                  onMouseLeave={() => {
                    setHoveredId(null);
                    setHoveredRect(null);
                  }}
                >
                  <span className="border-b border-dotted border-gray-400 pb-0.5 hover:text-blue-600 transition-colors">{ind.nombre}</span>
                </td>
                <td className="py-3 px-4 text-gray-600 text-[13px] align-middle">{formatFrecuencia(ind.frecuencia)}</td>
                <td className="py-3 px-4 align-middle">
                  {!ind.ultimaEvaluacion ? (
                    <span className="text-[12px] text-gray-400 italic">Sin evaluar</span>
                  ) : (
                    <span className={`admin-chip ${ind.estado === 'CUMPLE' ? 'admin-chip-green' : 'admin-chip-red'}`}>
                      {ind.estado === 'CUMPLE' ? 'Cumple' : 'No cumple'}
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-gray-700 font-medium align-middle">{formatValor(ind.resultado, ind.tipoResultado)}</td>
                <td className="py-3 px-4 text-gray-500 align-middle">{formatValor(ind.meta, ind.tipoResultado)}</td>
                <td className="py-3 px-4 align-middle">{renderUltimaRevision(ind)}</td>
                <td className="py-3 px-4 text-right align-middle">
                  <button
                    onClick={() => navigate(`/empresa/${nit}/indicador/${ind.id}`)}
                    className="admin-btn primary sm hover:scale-[1.02] active:scale-[0.98] transition-all"
                  >
                    Evaluar
                  </button>
                </td>
              </tr>
            ))}
            {filteredIndicadores.length === 0 && (
              <tr>
                <td colSpan={7} className="py-8 text-center text-gray-400 text-xs">
                  Ningún indicador coincide con los filtros aplicados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Floating Hover detail card outside overflow container to prevent clipping */}
      {activeHoveredInd && hoveredRect && (
        <div
          className="fixed p-4 bg-white border border-gray-200 rounded-xl shadow-xl text-xs space-y-2.5 text-left pointer-events-none transition-all duration-200"
          style={getTooltipStyle(hoveredRect)}
        >
          <p className="font-bold text-gray-800 text-[13px] border-b border-gray-100 pb-1">{activeHoveredInd.nombre}</p>

          <div>
            <span className="font-semibold text-gray-400 uppercase tracking-wider text-[9px] block mb-0.5">Fórmula Teórica</span>
            <p className="text-gray-700 bg-gray-50 p-2 rounded-lg font-mono border border-gray-100 leading-relaxed text-[11px]">
              {activeHoveredInd.formula}
            </p>
          </div>

          {activeHoveredInd.fuenteDatos && (
            <div>
              <span className="font-semibold text-gray-400 uppercase tracking-wider text-[9px] block mb-0.5">Fuente de Datos</span>
              <p className="text-gray-600 leading-relaxed">{activeHoveredInd.fuenteDatos}</p>
            </div>
          )}

          <div className="flex gap-6 pt-2 border-t border-gray-100">
            <div>
              <span className="font-semibold text-gray-400 uppercase tracking-wider text-[9px] block">Frecuencia</span>
              <span className="text-gray-700 font-semibold">{formatFrecuencia(activeHoveredInd.frecuencia)}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-400 uppercase tracking-wider text-[9px] block">Meta</span>
              <span className="text-gray-700 font-semibold">{formatValor(activeHoveredInd.meta, activeHoveredInd.tipoResultado)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
